import type { RequestOptions } from '@@/plugin-request/request';
import type { RequestConfig } from '@umijs/max';
import { history } from '@umijs/max';
import { message } from 'antd';
import {
  saveCurrentOrganizationUuid,
  saveCurrentTenantUuid,
} from '@/adapters/tenantStorage';
import { throwIfOwnBffFailed } from '@/services/bff/envelope';
import {
  applyRequestContextHeaders,
  assertRequestContextStillValid,
  attachRequestContext,
  isRequestContextAbortedError,
  readRequestContextFromError,
  resolveRequestContext,
} from '@/services/requestContext';
import { hasHttpResponseCode } from '@/utils/httpError';
import {
  clearOAuth2Tokens,
  ensureValidAccessToken,
  getAccessToken,
  redirectToSSO,
} from './services/oauth2';

export {
  getRequestOrganizationUuid,
  getRequestTenantUuid,
} from '@/services/requestContext';

type NormalizedRequestError = {
  status?: number;
  message: string;
  original: unknown;
};

type RequestErrorLike = {
  name?: string;
  message?: string;
  info?: {
    errorCode?: number;
    errorMessage?: string;
  };
  response?: {
    status?: number;
    data?: any;
  };
  config?: RequestOptions;
  request?: unknown;
  handled?: boolean;
};

const LOGIN_PATH = '/user/login';
const AUTH_ENDPOINTS_SKIP_REFRESH = [
  '/bff/auth/refreshToken',
  '/bff/auth/codeLogin',
  '/bff/auth/logout',
];
const AUTH_ENDPOINTS_SKIP_AUTH_HEADER = [
  '/bff/auth/refreshToken',
  '/bff/auth/codeLogin',
];

const getRequestPathname = (url?: string) => {
  if (!url) return '';

  try {
    const base =
      typeof window !== 'undefined'
        ? window.location.origin
        : 'http://localhost';
    return new URL(url, base).pathname;
  } catch {
    return url;
  }
};

const matchesAuthEndpoint = (url: string | undefined, endpoints: string[]) => {
  const pathname = getRequestPathname(url);
  return endpoints.some((endpoint) => pathname.includes(endpoint));
};

const handleUnauthorized = () => {
  clearOAuth2Tokens();
  if (
    history.location.pathname !== LOGIN_PATH &&
    history.location.pathname !== '/callback'
  ) {
    redirectToSSO();
  } else if (history.location.pathname !== LOGIN_PATH) {
    history.push(LOGIN_PATH);
  }
};

const extractResponseMessage = (responseData: any): string => {
  if (!responseData) {
    return '';
  }

  if (Array.isArray(responseData.message)) {
    return responseData.message.join(', ');
  }

  return (
    responseData.message ||
    responseData.errorMessage ||
    responseData.error ||
    responseData.msg ||
    ''
  );
};

const normalizeRequestError = (
  error: RequestErrorLike,
): NormalizedRequestError => {
  if (error.name === 'BizError') {
    const bizStatus = error.info?.errorCode;
    return {
      status: bizStatus,
      message: error.info?.errorMessage || error.message || '请求失败',
      original: error,
    };
  }

  if (error.response) {
    const status = error.response.status;
    const responseMessage = extractResponseMessage(error.response.data);
    return {
      status,
      message:
        responseMessage || (status ? `请求失败 (${status})` : '请求失败'),
      original: error,
    };
  }

  return {
    message: error.message || '请求错误，请重试',
    original: error,
  };
};

/**
 * @name 错误处理
 * @doc https://umijs.org/docs/max/request#配置
 */
export const errorConfig: RequestConfig = {
  errorConfig: {
    errorThrower: (res) => {
      throwIfOwnBffFailed(res);
    },
    errorHandler: (error: RequestErrorLike, opts: any) => {
      const snapshot = readRequestContextFromError(error, opts);
      if (snapshot) {
        assertRequestContextStillValid(snapshot);
      }
      if (opts?.skipErrorHandler) throw error;
      if (isRequestContextAbortedError(error)) throw error;

      const normalized = normalizeRequestError(error);

      if (hasHttpResponseCode(error, 10010)) {
        const lastReload = sessionStorage.getItem(
          'last_tenant_mismatch_reload',
        );
        const now = Date.now();
        if (lastReload && now - Number(lastReload) < 8000) {
          sessionStorage.removeItem('last_tenant_mismatch_reload');
          handleUnauthorized();
          throw error;
        }
        sessionStorage.setItem('last_tenant_mismatch_reload', String(now));

        message.error('租户环境已变更，系统将自动刷新');
        setTimeout(() => {
          saveCurrentTenantUuid(null);
          saveCurrentOrganizationUuid(null);
          clearOAuth2Tokens();
          window.location.reload();
        }, 1500);
        throw error;
      }

      if (normalized.status === 401) {
        handleUnauthorized();
        throw error;
      }

      if (error.response) {
        message.error(normalized.message);
      } else if (error.request) {
        message.error('网络错误，请检查网络连接后重试');
      } else {
        message.error(normalized.message);
      }

      error.handled = true;
      throw error;
    },
  },

  requestInterceptors: [
    async (config: RequestOptions) => {
      const requestUrl = config?.url as string | undefined;
      const skipTokenRefresh = matchesAuthEndpoint(
        requestUrl,
        AUTH_ENDPOINTS_SKIP_REFRESH,
      );
      const skipAuthHeader = matchesAuthEndpoint(
        requestUrl,
        AUTH_ENDPOINTS_SKIP_AUTH_HEADER,
      );
      if (skipAuthHeader) {
        return config;
      }

      const snapshot = resolveRequestContext(config, { requirePark: false });
      const nextConfig = attachRequestContext(config, snapshot);
      const token = skipTokenRefresh
        ? getAccessToken()
        : await ensureValidAccessToken();
      assertRequestContextStillValid(snapshot);

      const headers = applyRequestContextHeaders(
        { ...(nextConfig.headers as Record<string, string> | undefined) },
        snapshot,
      );
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      nextConfig.headers = headers;
      return nextConfig;
    },
  ],
};
